import { nanoid } from 'nanoid'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Animated, NativeScrollEvent, NativeSyntheticEvent, View } from 'react-native'
import { useSearchParams } from 'react-router-dom'

import usePrevious from '@common/hooks/usePrevious'
import useRoute from '@common/hooks/useRoute'
import useTheme from '@common/hooks/useTheme'
import spacings, { SPACING_SM } from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { TAB_CONTENT_WIDTH } from '@web/constants/spacings'

import { RADII } from '@common/modules/dashboard/home-kit/tokens'

import Activity from '../Activity'
import Collections from '../Collections'
import DeFiPositions from '../DeFiPositions'
import { TabType } from '../TabsAndSearch/Tabs/Tab/Tab'
import Tokens from '../Tokens'
import useAppMode from '@web/hooks/useAppMode'
import useRailgunContext from '@web/hooks/useRailgunContext'

interface Props {
  onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
  animatedOverviewHeight: Animated.Value
}

const DashboardPages = ({ onScroll, animatedOverviewHeight }: Props) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { mode } = useAppMode()
  const { currentRailgunWallet } = useRailgunContext()
  const route = useRoute()
  const [sessionId] = useState(`dashboard-${nanoid()}`)
  const [searchParams, setSearchParams] = useSearchParams()
  const { dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { networks } = useNetworksControllerState()
  const { dispatch } = useBackgroundService()

  const [openTab, setOpenTab] = useState(() => {
    const params = new URLSearchParams(route?.search)

    return (params.get('tab') as TabType) || 'tokens'
  })
  const prevOpenTab = usePrevious(openTab)

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'tokens' || tab === 'collectibles' || tab === 'defi' || tab === 'activity') {
      setOpenTab((current) => (current === tab ? current : tab))
    }
  }, [searchParams])
  const prevMode = usePrevious(mode)
  const prevCurrentRailgunWallet = usePrevious(currentRailgunWallet)

  useEffect(() => {
    if (prevMode !== undefined && prevMode !== mode) {
      setOpenTab('tokens')
      setSearchParams((prev) => {
        prev.set('tab', 'tokens')
        return prev
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, prevMode, prevCurrentRailgunWallet])

  // To prevent initial load of all tabs but load them when requested by the user
  // Persist the rendered list of items for each tab once opened
  // This technique improves the initial loading speed of the dashboard
  const [initTab, setInitTab] = useState<{
    [key: string]: boolean
  }>({})

  const network = useMemo(() => {
    if (!dashboardNetworkFilter || dashboardNetworkFilter === 'rewards') return null

    const result = networks.find(({ chainId }) => chainId === BigInt(dashboardNetworkFilter))

    return result || null
  }, [dashboardNetworkFilter, networks])

  const dashboardNetworkFilterName = useMemo(() => {
    if (!dashboardNetworkFilter) return null

    if (dashboardNetworkFilter === 'rewards') return t('Rewards')

    const result = networks.find(({ chainId }) => chainId === BigInt(dashboardNetworkFilter))

    return result?.name || null
  }, [dashboardNetworkFilter, networks, t])

  useEffect(() => {
    if (openTab !== prevOpenTab && !initTab?.[openTab]) {
      setInitTab((prev) => ({ ...prev, [openTab]: true }))
    }
  }, [openTab, prevOpenTab, initTab])

  useEffect(() => {
    // Initialize the port session. This is necessary to automatically terminate the session when the tab is closed.
    // The process is managed in the background using port.onDisconnect,
    // as there is no reliable window event triggered when a tab is closed.
    setSearchParams((prev) => {
      prev.set('sessionId', sessionId)
      return prev
    })

    return () => {
      // Remove session - this will be triggered only when navigation to another screen internally in the extension.
      // The session removal when the window is forcefully closed is handled
      // in the port.onDisconnect callback in the background.
      dispatch({ type: 'MAIN_CONTROLLER_ACTIVITY_RESET_ACC_OPS_FILTERS', params: { sessionId } })
    }
    // setSearchParams must not be in the dependency array
    // as it changes on call and kills the session prematurely
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId])

  return (
    <View style={[flexbox.flex1, spacings.phSm]}>
      {/* home-v2 rounded card shell — ONE shell wraps whichever tab is active so
          there is always exactly one card and never an empty one above the tab row
          (the per-tab bodies below toggle visibility via display:none, so only the
          active one occupies the flex:1 space inside the shell). Owning flex:1 here
          makes the card fill all remaining vertical space for BOTH public and private.

          The CONTAINER carries the inset, not the rows: the parent's horizontal
          padding (spacings.phSm — same as the banner) holds the card off the window
          edges, while paddingHorizontal here gives the rows breathing room from the
          card border (aligning them with the banner's inner content). The card fills
          the padded parent via width:'100%' — note we must NOT also add a horizontal
          margin, or width:'100%'+margin would overflow the parent and the card would
          bleed past the right edge. maxWidth + centered keeps it aligned with the
          banner in the wide full-tab layout. */}
      <View
        style={[
          flexbox.flex1,
          {
            // The card carries the rows' horizontal inset via its own paddingHorizontal
            // (the list fills the card with flex:1 + height:100% — see
            // DashboardPageScrollContainer — so this padding is respected). The
            // window-edge inset comes from the parent's phSm.
            paddingHorizontal: SPACING_SM,
            marginBottom: 10,
            width: '100%',
            maxWidth: TAB_CONTENT_WIDTH,
            alignSelf: 'center',
            backgroundColor: theme.homeCardBackground as string,
            borderWidth: 1,
            borderColor: theme.homeCardBorder as string,
            borderRadius: RADII.lg,
            overflow: 'hidden'
          }
        ]}
      >
        <Tokens
          openTab={openTab}
          sessionId={sessionId}
          setOpenTab={setOpenTab}
          onScroll={onScroll}
          initTab={initTab}
          dashboardNetworkFilterName={dashboardNetworkFilterName}
          animatedOverviewHeight={animatedOverviewHeight}
        />
        <Collections
          openTab={openTab}
          sessionId={sessionId}
          setOpenTab={setOpenTab}
          initTab={initTab}
          onScroll={onScroll}
          networks={networks}
          dashboardNetworkFilterName={dashboardNetworkFilterName}
          animatedOverviewHeight={animatedOverviewHeight}
        />

        <DeFiPositions
          openTab={openTab}
          sessionId={sessionId}
          setOpenTab={setOpenTab}
          onScroll={onScroll}
          initTab={initTab}
          dashboardNetworkFilterName={dashboardNetworkFilterName}
          animatedOverviewHeight={animatedOverviewHeight}
        />

        <Activity
          openTab={openTab}
          sessionId={sessionId}
          setOpenTab={setOpenTab}
          onScroll={onScroll}
          initTab={initTab}
          animatedOverviewHeight={animatedOverviewHeight}
          network={network}
        />
      </View>
    </View>
  )
}

export default React.memo(DashboardPages)
